import { APP_COLOR_HEX } from "./theme";
// // https://medium.com/better-programming/generate-contrasting-text-for-your-random-background-color-ac302dc87b4
type RGB = { r: number; g: number; b: number };

interface Options {
  threshold?: number;
  black?: string;
  white?: string;
}

const rgbToYIQ = ({ r, g, b }: RGB): number =>
  (r * 299 + g * 587 + b * 114) / 1000;

const hexToRgb = (hex: string): RGB | undefined => {
  if (!hex || hex === undefined || hex === "") {
    return undefined;
  }

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : undefined;
};

/**
 * Convert hex color to rgba with opacity
 * Supports both 3-digit (#fff) and 6-digit (#ffffff) hex formats
 */
export const hexToRgba = (hex: string, opacity: number): string => {
  // Remove # if present
  let cleanHex = hex.replace("#", "");

  // Expand 3-digit hex to 6-digit
  if (cleanHex.length === 3) {
    cleanHex =
      cleanHex[0] +
      cleanHex[0] +
      cleanHex[1] +
      cleanHex[1] +
      cleanHex[2] +
      cleanHex[2];
  }

  const rgb = hexToRgb(cleanHex);
  if (!rgb) {
    return `rgba(0, 0, 0, ${opacity})`;
  }
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`;
};

export const rgbToHex = (rgb: string | undefined): string | undefined => {
  if (!rgb || rgb === undefined || rgb === "") {
    return undefined;
  }

  const standardRgb = rgb.split("(")[1]?.split(")")[0] || rgb;
  const rgbArray = standardRgb.split(",");

  return (
    "#" +
    rgbArray
      .map((e) => {
        const x = parseInt(e).toString(16);

        return x.length == 1 ? "0" + x : x;
      })
      .join("")
  );
};

export const isDarkTextColor = (
  colorHex: string,
  options: Options = {}
): boolean => {
  const threshold = options.threshold || 150;

  if (colorHex === undefined) {
    return true;
  }

  const rgb = hexToRgb(colorHex);

  if (rgb === undefined) {
    return true;
  }

  return rgbToYIQ(rgb) >= threshold;
};

export default (
  colorHex: string | null | undefined,
  options: Options = {}
): string => {
  const _options = {
    threshold: 150,
    black: APP_COLOR_HEX.BLACK,
    white: APP_COLOR_HEX.WHITE,
    ...options,
  };

  return !colorHex || isDarkTextColor(colorHex, _options)
    ? _options.black
    : _options.white;
};

export { APP_COLOR_HEX };
